import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { naraEvents as initialEvents } from '../mocks/events';
import type { PrefectureEvent } from '../mocks/events';
import { supabase } from '../lib/supabase';

const LS_KEY = 'admin_events_data';

function loadFromStorage(): PrefectureEvent[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore
  }
  return initialEvents;
}

function saveToStorage(data: PrefectureEvent[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

interface EventStoreContextType {
  events: PrefectureEvent[];
  addEvent: (event: PrefectureEvent) => void;
  updateEvent: (id: number | string, data: Partial<PrefectureEvent>) => void;
  deleteEvent: (id: number | string) => void;
  setEvents: (events: PrefectureEvent[]) => void;
  loading: boolean;
  refreshEvents: () => Promise<void>;
}

const EventStoreContext = createContext<EventStoreContextType | undefined>(undefined);

export function EventStoreProvider({ children }: { children: ReactNode }) {
  const [events, setEventsState] = useState<PrefectureEvent[]>(loadFromStorage);
  const [loading, setLoading] = useState(false);

  // Fetch events from DB (future implementation)
  const fetchEventsFromDB = useCallback(async (): Promise<PrefectureEvent[]> => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('event_date', { ascending: true });

      if (error) throw error;

      // Transform DB data to PrefectureEvent format
      return (data || []).map((item: any) => ({
        id: item.id, // UUID文字列のまま保持
        title: item.title || '',
        date: item.date || '',
        time: item.time || '',
        venue: item.venue || '',
        address: item.address || '',
        prefecture: item.prefecture || 'nara',
        description: item.description || '',
        price: item.price || '',
        category: item.category || 'LIVE',
        thumbnail: item.thumbnail || item.image_url || '',
        gallery: item.gallery || [],
        artistIds: item.artist_ids || [],
        artistNames: item.artist_names || [],
        isFeatured: item.is_featured || false,
        color: item.color || '#00d9ff',
        attributeId: item.attribute_id || '',
        mainGenreId: item.main_genre_id || '',
        mainPartId: item.main_part_id || '',
        is_paid_event: item.is_paid_event || false,
        ticket_price: item.ticket_price ?? null,
        max_capacity: item.max_capacity ?? null,
        current_reservations: item.current_reservations || 0,
        sold_out: item.sold_out || false,
      }));
    } catch (err) {
      console.error('Failed to fetch events from DB:', err);
      return [];
    }
  }, []);

  // Merge local and DB events
  const refreshEvents = useCallback(async () => {
    setLoading(true);
    try {
      const dbEvents = await fetchEventsFromDB();
      const localEvents = loadFromStorage();

      // DBイベントのIDセット（UUID文字列）
      const dbEventIds = new Set(dbEvents.map(e => String(e.id)));
      // ローカルのモックデータ（数値ID）はDBに存在しないものだけ残す
      const uniqueLocalEvents = localEvents.filter(e => !dbEventIds.has(String(e.id)));
      const mergedEvents = [...dbEvents, ...uniqueLocalEvents];

      setEventsState(mergedEvents);
      saveToStorage(mergedEvents);
    } catch (err) {
      console.error('Failed to refresh events:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchEventsFromDB]);

  // Initial load: try to fetch from DB
  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    saveToStorage(events);
  }, [events]);

  const setEvents = useCallback((newEvents: PrefectureEvent[]) => {
    setEventsState(newEvents);
  }, []);

  const addEvent = useCallback((event: PrefectureEvent) => {
    setEventsState((prev) => [...prev, event]);
  }, []);

  const updateEvent = useCallback((id: number | string, data: Partial<PrefectureEvent>) => {
    setEventsState((prev) =>
      prev.map((e) => (String(e.id) === String(id) ? { ...e, ...data } as PrefectureEvent : e))
    );
  }, []);

  const deleteEvent = useCallback((id: number | string) => {
    setEventsState((prev) => prev.filter((e) => String(e.id) !== String(id)));
  }, []);

  return (
    <EventStoreContext.Provider value={{ events, addEvent, updateEvent, deleteEvent, setEvents, loading, refreshEvents }}>
      {children}
    </EventStoreContext.Provider>
  );
}

export function useEventStore(): EventStoreContextType {
  const ctx = useContext(EventStoreContext);
  if (ctx === undefined) {
    throw new Error('useEventStore must be used within an EventStoreProvider');
  }
  return ctx;
}
